import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Calendar, Mail, Phone, Shield, ShieldCheck, User as UserIcon, MapPin, Clock, Edit, Power, PowerOff } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useUser } from '@/hooks/useUsers'
import { useAuth } from '@/contexts/AuthContext'
import { UserRole } from '@/models/user.model'
import { usersService } from '@/services/usersService'
import { useNotifications } from '@/contexts/NotificationContext'
import { paths } from '@/routes/path'
import { cn } from '@/utils/cn'

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { addNotification } = useNotifications()
  const { user: currentUser } = useAuth()

  const {
    data: user,
    isLoading,
    isError,
    error,
  } = useUser(userId)
  const [pendingStatus, setPendingStatus] = useState<'activate' | 'deactivate' | null>(null)
  const isStatusDialogOpen = Boolean(pendingStatus)

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      return usersService.toggleUserStatus(id, isActive)
    },
    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
      if (updatedUser.id) {
        queryClient.invalidateQueries({ queryKey: ['users', 'detail', updatedUser.id] })
      }
      addNotification({
        title: 'Status updated',
        message: `${updatedUser.fullName || 'User'} is now ${updatedUser.isActive ? 'active' : 'inactive'}.`,
        type: 'general',
        priority: 'low',
        isRead: false,
        isArchived: false,
      })
    },
    onError: (mutationError: unknown) => {
      const message = mutationError instanceof Error ? mutationError.message : 'Unable to update status'
      addNotification({
        title: 'Update failed',
        message,
        type: 'general',
        priority: 'medium',
        isRead: false,
        isArchived: false,
      })
    },
  })

  const requestStatusChange = () => {
    if (!user?.id || toggleStatusMutation.isPending || !canManageStatus) return
    setPendingStatus(user.isActive ? 'deactivate' : 'activate')
  }

  const handleConfirmStatusChange = () => {
    if (!user?.id || !pendingStatus || !canManageStatus) return
    toggleStatusMutation.mutate({ id: user.id, isActive: pendingStatus === 'activate' })
    setPendingStatus(null)
  }

  const handleCancelStatusChange = () => {
    setPendingStatus(null)
  }

  const formatDateTime = (value: string | null) => {
    if (!value) return '—'
    try {
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    } catch {
      return '—'
    }
  }

  const roleBadgeColor = (role: UserRole | null) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'bg-rose-50 text-rose-700'
      case UserRole.INSTRUCTOR:
        return 'bg-blue-50 text-blue-700'
      case UserRole.LEARNER:
        return 'bg-emerald-50 text-emerald-700'
      default:
        return 'bg-secondary-50 text-secondary-700'
    }
  }

  const statusBadgeColor = (isActive: boolean | null) =>
    isActive
      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
      : 'bg-secondary-100 text-secondary-600 border border-secondary-200'

  const summaryItems = useMemo(
    () => [
      {
        icon: Mail,
        label: 'Email',
        value: user?.email || '—',
      },
      {
        icon: Phone,
        label: 'Phone',
        value: user?.phone || '—',
      },
      {
        icon: Calendar,
        label: 'Joined',
        value: formatDateTime(user?.createdAt ?? null),
      },
      {
        icon: Clock,
        label: 'Last login',
        value: formatDateTime(user?.lastLoginAt ?? null),
      },
      {
        icon: Shield,
        label: 'Role',
        value: user?.role ?? '—',
      },
      {
        icon: MapPin,
        label: 'Department',
        value: user?.programmeTitle || user?.departmentName || '—',
      },
    ],
    [user, formatDateTime],
  )

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (isError || !user) {
    const message = error instanceof Error ? error.message : 'We could not load this user.'
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <p className="text-lg font-semibold text-secondary-900">User not found</p>
        <p className="mt-1 max-w-md text-sm text-secondary-600">{message}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate(paths.app.users.root.absolute)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to users
        </Button>
      </div>
    )
  }

  const canEditUser = currentUser?.role === UserRole.ADMIN
  const canManageStatus = currentUser?.role === UserRole.ADMIN
  const editPath = user.id && canEditUser ? paths.app.users.edit.build(user.id) : null

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="text-secondary-600"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <div className="flex flex-wrap items-center gap-3">
            {editPath && (
              <Link to={editPath}>
                <Button variant="ghost" className="text-secondary-600 hover:text-secondary-900">
                  <Edit className="mr-2 h-4 w-4" />
                  Edit user
                </Button>
              </Link>
            )}
            {user.id && user.role === UserRole.LEARNER && currentUser?.role === UserRole.ADMIN && (
              <Link to={paths.app.users.licenses.build(user.id)}>
                <Button variant="outline" className="text-primary-700 border-primary-100 hover:bg-primary-50">
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Licenses
                </Button>
              </Link>
            )}
            {canManageStatus && (
              <Button
                variant="ghost"
                className={cn(
                  'text-secondary-600 hover:text-secondary-900',
                  toggleStatusMutation.isPending && 'opacity-60',
                )}
                disabled={toggleStatusMutation.isPending || !user.id}
                onClick={requestStatusChange}
              >
                {user.isActive ? (
                  <>
                    <Power className="mr-2 h-4 w-4 text-emerald-500" />
                    Deactivate
                  </>
                ) : (
                  <>
                    <PowerOff className="mr-2 h-4 w-4" />
                    Activate
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
        
        <Card className="border border-secondary-100 shadow-sm">
          <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
            {user.avatar ? (
              <img
                src={user.avatar}
                alt={user.fullName}
                className="h-20 w-20 rounded-full object-cover ring-2 ring-primary-100"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-secondary-100 text-xl font-semibold text-secondary-600">
                {user.initials || <UserIcon className="h-8 w-8" />}
              </div>
            )}
            <div>
              <CardTitle className="text-2xl font-bold text-secondary-900">
                {user.fullName || 'Unnamed user'}
              </CardTitle>
              <CardDescription className="text-secondary-600">
                {user.programmeTitle || user.departmentName || 'No department assigned'}
              </CardDescription>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge className={cn('rounded-full px-3 py-1 text-xs font-semibold capitalize', roleBadgeColor(user.role))}>
                  {user.role ?? '—'}
                </Badge>
                <Badge className={cn('rounded-full px-3 py-1 text-xs font-semibold capitalize', statusBadgeColor(user.isActive))}>
                  {user.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 gap-4 border-b border-secondary-100 pb-6 sm:grid-cols-2 lg:grid-cols-3">
            {summaryItems.map((item) => (
              <div key={item.label} className="flex items-start gap-3 rounded-lg bg-secondary-50 px-4 py-3">
                <item.icon className="mt-0.5 h-4 w-4 text-primary-600" />
                <div>
                  <p className="text-sm font-medium text-secondary-700">{item.label}</p>
                  <p className="text-sm text-secondary-600">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* <div className="space-y-3">
            <h3 className="text-lg font-semibold text-secondary-900">About</h3>
            <p className="text-sm text-secondary-600 leading-6">
              {user.bio || 'No biography added for this user yet.'}
            </p>
          </div> */}

          {/* <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-secondary-100 bg-white px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Student ID</p>
              <p className="mt-1 text-sm text-secondary-700">{user.studentId || '—'}</p>
            </div>
            <div className="rounded-lg border border-secondary-100 bg-white px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Enrollment date</p>
              <p className="mt-1 text-sm text-secondary-700">
                {formatDateTime(user.enrollmentDate ?? null)}
              </p>
            </div>
          </div> */}
        </CardContent>
      </Card>
    </div>
    <ConfirmDialog
      open={isStatusDialogOpen}
      title={pendingStatus === 'activate' ? 'Activate user?' : 'Deactivate user?'}
      description={
        pendingStatus
          ? `Are you sure you want to ${pendingStatus} ${user.fullName || 'this user'}?`
          : undefined
      }
      confirmLabel={pendingStatus === 'activate' ? 'Activate' : 'Deactivate'}
      confirmTone={pendingStatus === 'activate' ? 'default' : 'destructive'}
      onConfirm={handleConfirmStatusChange}
      onCancel={handleCancelStatusChange}
    />
    </>
  )
}
